import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import GenericFormDrawer from '../../components/GenericFormDrawer';
import Button from '../../components/ui/Button';
import { formatDate, formatDateTime } from '../../utils/Utils';
import { reasonList, useReminderApi } from './config';

// Faz 2 Plan 2 (spec §5) — ortak toplu hatırlatma çekmecesi: açılınca önizleme (alıcılar, atlananlar, seçim dışı sayıları,
// sessiz saat), onayda önizleme özet anahtarıyla gönderim. Önizlemeden sonra küme değişirse (409) önizleme yenilenir ve
// uyarı gösterilir; gönderim yapılmaz. Kabuk GenericFormDrawer (modal yok); liste çekmecenin tek kaydırmasında akar.
// `selection = { periodId, taskIds?, criteria?, sourceInsightId?, sourceActionId? }`.
const Summary = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
`;

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Notice = styled.div`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border-left: 3px solid ${({ theme, $tone }) => ($tone === 'danger' ? theme.colors.auxiliar.danger : theme.colors.auxiliar.warning)};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const SectionTitle = styled.h4`
  margin: 16px 0 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

const Outcome = styled.span`
  align-self: flex-start;
  white-space: nowrap;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme, $pending }) => ($pending ? theme.colors.secondary.dark : theme.colors.text.muted)};
`;

const LoadMore = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;
`;

function Reasons({ title, counts, prefix }) {
  const { t } = useTranslation();
  const list = reasonList(counts);
  if (list.length === 0) return null;
  return (
    <>
      <SectionTitle>{title}</SectionTitle>
      {list.map(({ code, count }) => (
        <Row key={code} data-reason={code}>
          <span>{t(`${prefix}.${code}`, { defaultValue: code })}</span>
          <span>{count}</span>
        </Row>
      ))}
    </>
  );
}

// `headerContent` (isteğe bağlı): çekmecenin en üstünde çizilen çağırana özgü seçenekler (ör. E17 "Ekibe Hatırlat"ın
// "Yalnız Gecikmiş" kutusu). Seçenek seçimi değiştirirse çağıran yeni `selection` verir; önizleme kendiliğinden yenilenir.
export default function ReminderDrawer({ open, onClose, selection, onSent, headerContent = null }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { preview, confirm } = useReminderApi();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [changed, setChanged] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const d = await preview(selection, 1);
    setData(d);
    setRows(d?.recipients?.data || []);
    setPage(1);
    setLoading(false);
  }, [preview, selection]);

  useEffect(() => {
    if (open) {
      setChanged(false);
      load();
    } else {
      setData(null);
      setRows([]);
    }
  }, [open, load]);

  const loadMore = useCallback(async () => {
    const next = page + 1;
    setLoading(true);
    const d = await preview(selection, next, data?.previewHash);
    setLoading(false);
    if (!d) return;
    // Sunucu görüntüyü yenilediyse (belirteç süresi doldu ya da başka sekmede yeni önizleme açıldı) baştan önizle ve söyle.
    if (data && d.previewHash !== data.previewHash) {
      setChanged(true);
      await load();
      return;
    }
    // Sayfalar canlı çözümden gelir; küme sayfalar arasında kaydıysa aynı kişi iki kez görünmesin.
    setRows((r) => {
      const seen = new Set(r.map((x) => x.recipientEmployeeId));
      return [...r, ...(d.recipients?.data || []).filter((x) => !seen.has(x.recipientEmployeeId))];
    });
    setPage(next);
  }, [page, preview, selection, data, load]);

  const handleSave = useCallback(async () => {
    if (!data) return false;
    const r = await confirm(selection, data.previewHash);
    if (r.ok) {
      onSent?.(r.result);
      return true;
    }
    if (r.stale) {
      setChanged(true);
      await load();
    }
    return false;
  }, [confirm, selection, data, load, onSent]);

  const recipientCount = data?.recipientCount || 0;
  const total = data?.recipients?.totalRowCount || 0;

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={t('performance.reminder.title')}
      schema={[]}
      submitLabel={t('performance.reminder.send', { count: recipientCount })}
      submitDisabled={loading || !data || recipientCount === 0}
      onSave={handleSave}
    >
      {headerContent}
      {changed ? <Notice $tone="danger" data-notice="changed">{t('performance.reminder.changedNotice')}</Notice> : null}
      {data?.quietHoursUntil ? (
        <Notice data-notice="quiet">{t('performance.reminder.quietHours', { time: formatDateTime(data.quietHoursUntil, lang) })}</Notice>
      ) : null}
      {data ? (
        <>
          <Summary>
            <div>
              <SummaryValue data-summary="recipients">{recipientCount}</SummaryValue>
              <SummaryLabel>{t('performance.reminder.recipients')}</SummaryLabel>
            </div>
            <div>
              <SummaryValue data-summary="tasks">{data.taskCount || 0}</SummaryValue>
              <SummaryLabel>{t('performance.reminder.tasks')}</SummaryLabel>
            </div>
          </Summary>
          {recipientCount === 0 ? <Notice data-notice="empty">{t('performance.reminder.noRecipients')}</Notice> : null}
          <Reasons title={t('performance.reminder.skippedTitle')} counts={data.skipped} prefix="performance.reminder.reason" />
          <Reasons title={t('performance.reminder.excludedTitle')} counts={data.excluded} prefix="performance.reminder.reason" />
          {rows.length > 0 ? <SectionTitle>{t('performance.reminder.recipientList')}</SectionTitle> : null}
          {rows.map((r) => (
            <Row key={`${r.recipientEmployeeId}`} data-recipient={r.recipientEmployeeId}>
              <div>
                <div>{r.name || '-'}</div>
                <Muted>
                  {[r.title, r.role ? t(`performance.reminder.role.${r.role}`, { defaultValue: r.role }) : null].filter(Boolean).join(' · ')}
                </Muted>
                {r.onBehalfOfName ? <Muted>{t('performance.reminder.onBehalfOf', { name: r.onBehalfOfName })}</Muted> : null}
                <Muted>
                  {t('performance.reminder.taskCount', { count: r.taskCount })}
                  {r.nearestDueAt ? ` · ${t('performance.reminder.nearestDue', { date: formatDate(r.nearestDueAt, lang) })}` : ''}
                </Muted>
              </div>
              <Outcome $pending={r.outcome === 'pending'}>{t(`performance.reminder.outcome.${r.outcome}`, { defaultValue: r.outcome })}</Outcome>
            </Row>
          ))}
          {rows.length < total ? (
            <LoadMore>
              <Button variant="outline" onClick={loadMore} disabled={loading}>{t('performance.reminder.loadMore')}</Button>
            </LoadMore>
          ) : null}
        </>
      ) : null}
    </GenericFormDrawer>
  );
}
